<template>
  <div class="col d-flex">
    <div
      v-if="!isHidden"
      class="alert flex-grow-1"
      :class="`insights-box insights-box-${alertColor}-default`"
    >
      <div
        class="px-5 d-flex flex-column justify-content-between w-100 h-100 pb-3"
      >
        <div v-if="hasHideButton" class="hide-button-wrapper hide-fullscreen">
          <button class="btn btn-link p-0" @click="isHidden = !isHidden">
            <i class="bi bi-x fs-1 p-0"></i>
          </button>
        </div>
        <div class="insights-header">
          <div v-if="titleText" class="d-flex align-items-center py-3 mb-5">
            <i class="me-3 insight-icon" :class="iconForInsight"></i>
            <div
              class="d-flex justify-content-between align-items-center flex-grow-1"
            >
              <p
                class="widget-title mb-0 fs-3 py-1 px-2 py-sm-3 px-sm-5"
                :class="callback ? 'has-callback' : ''"
                @click="callback ? callback(insight) : null"
              >
                {{ titleText }}
              </p>
              <i
                v-if="infoText && !shouldHideDetailedInfo"
                :title="infoText"
                data-bs-toggle="tooltip"
                data-bs-placement="left"
                class="ms-1 insight-icon bi-info-circle-fill text-info fs-5"
              >
              </i>
            </div>
          </div>
          <div class="row insights-info-text mb-5 mb-md-10">
            <div class="col col-12" :class="largeContainerSize">
              <p v-if="feedbackText" class="fs-6">
                {{ feedbackText }}
              </p>
              <p v-if="detailedText" class="fs-6">
                {{ detailedText }}
              </p>
            </div>
            <div
              v-if="hasTrend && !shouldHideDetailedInfo"
              :class="smallContainerSize"
            >
              <LineChart
                :dataName="`insight-trend-details-line-${
                  insight.statItemName
                }-${isDetails ? 'details' : 'overview'}-${area}`"
                :data="lineChartData"
                :series="lineChartSeries"
                :options="lineChartOptions"
                theme="monochrome"
              />
            </div>
            <div
              v-else-if="valueForInsight && !shouldHideDetailedInfo"
              :class="smallContainerSize"
            >
              <div
                class="d-flex align-items-center justify-content-center flex-column h-100 insights-value-container"
              >
                <h1 class="insights-value">
                  {{ valueForInsight }}
                </h1>
                <small>
                  {{ $tr(insight.messages, 'title') }}
                </small>
              </div>
            </div>
            <div
              v-else-if="isQuestion && !shouldHideDetailedInfo"
              :class="smallContainerSize"
            >
              <BarChart
                :dataName="`insight-question-details-bar-${
                  insight.statItemName
                }-${isDetails ? 'details' : 'overview'}`"
                :data="barChartData"
                :numberOfResponses="insight.amount"
                :options="barChartOptions"
                theme="monochrome"
              />
            </div>
          </div>
        </div>
        <div>
          <div
            class="d-flex gap-3 justify-content-between mt-10 hide-fullscreen"
          >
            <div class="d-flex gap-3">
              <button
                v-for="link in linksForInsight"
                :key="link.title"
                class="btn btn-round btn-sm btn-secondary link-button"
              >
                <a class="text-dark" :href="link.href" target="_blank">
                  <i class="bi bi-box-arrow-up-right fs-5"></i>
                  {{ link.title }}
                </a>
              </button>
            </div>
            <div
              v-if="
                showMoreInsightsModal &&
                Object.keys(showMoreInsightsModal).length
              "
              class="d-flex justify-content-end"
            >
              <button
                class="btn btn-sm btn-secondary btn-round"
                @click="handleOpenModal"
              >
                {{ showMoreInsightsModal.title }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import BarChart from './BarChart'
// import LineChart from './LineChart'
import BarChart from '@/components/shared/charts/BarChart.vue'
import LineChart from '@/components/shared/charts/LineChart.vue'

const safeMessage = (message) => {
  return message && message !== 'null' ? message : ''
}
const valueToPercentage = (value) => {
  return `${Math.round(value * 10)}%`
}

export default {
  name: 'InsightsAlert',
  components: {
    BarChart,
    LineChart
  },
  props: {
    area: String,
    callback: {
      type: Function,
      default: () => null
    },
    messages: {
      type: Object,
      default: () => {}
    },
    isDetails: {
      type: Boolean,
      default: false
    },
    insight: Object,
    showMoreInsightsModal: {
      type: Object,
      default: () => {}
    },
    shouldHideDetailedInfo: {
      type: Boolean,
      default: false
    },
    hasHideButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isHidden: false
    }
  },
  computed: {
    alertColor() {
      switch (this.insight.result) {
        case 'low':
          return 'low'
        case 'trend_negative':
          return 'low'
        case 'medium':
          return 'medium'
        case 'high':
          return 'high'
        case 'trend_positive':
          return 'high'
        default:
          return 'none'
      }
    },
    barChartOptions() {
      return {
        largeFont: true
      }
    },
    barChartData() {
      const amounts = Object.keys(this.insight)
        .filter((k) => k.includes('amount_'))
        .sort((a, b) => a.split('_')[1] - b.split('_')[1])
      return amounts.map((amountKey) => {
        return {
          value: this.insight[amountKey],
          name: amountKey.split('_')[1]
        }
      })
    },
    feedbackText() {
      const insightMessage = this.$tr(this.insight.messages, `results.feedback`)
      const statItemType = this.$tr(
        this.messages,
        `statItemTypes.${this.insight.statName}.title`
      )
      return safeMessage(insightMessage).replace(
        /{statItemType}/g,
        statItemType
      )
    },
    detailedText() {
      const insight = this.insight
      const insightMessage = this.$tr(insight.messages, `results.detailed`)
      const generalMessage =
        this.$tr(this.messages, `results[${insight.result}].detailed`) ||
        insight.result
      const statItemType = this.$tr(
        this.messages,
        `statItemTypes.${insight.statName}.title`
      )
      const result = safeMessage(insightMessage) || safeMessage(generalMessage)
      return result.replace(/{statItemType}/g, statItemType)
    },
    hasTrend() {
      return (
        this.insight.result === 'trend_negative' ||
        this.insight.result === 'trend_positive'
      )
    },
    iconForInsight() {
      switch (this.insight.result) {
        case 'low':
          return 'bi-exclamation-triangle-fill insight-icon-low'
        case 'trend_negative':
          return 'bi-arrow-down-right insight-icon-low'
        case 'medium':
          return 'bi-info-circle-fill insight-icon-medium'
        case 'high':
          return 'bi-star-fill insight-icon-high'
        case 'trend_positive':
          return 'bi-arrow-up-right insight-icon-high'
        default:
          return 'none'
      }
    },
    infoText() {
      const insight = this.insight
      const insightMessage = this.$tr(
        this.messages,
        `results.${insight.result}.info`
      )
      const statItemType = this.$tr(
        this.messages,
        `statItemTypes.${insight.statName}.title`
      )
      const result = safeMessage(insightMessage)
      return result.replace(/{statItemType}/g, statItemType)
    },
    isQuestion() {
      return this.insight.statName === 'questions'
    },
    linksForInsight() {
      return Object.values(this.$tr(this.insight.messages, 'links') || {})
    },
    lineChartData() {
      return this.insight.averages.map((a) => {
        let value = a.value
        if (this.insight.statItemName === 'enps') {
          value = a.npsScore
        } else if (this.insight.statItemName === 'participation') {
          value = (a.value * 10).toFixed(0)
        }
        return {
          date: new Date(a.timestamp),
          [this.insight.statItemName]: { value }
        }
      })
    },
    lineChartSeries() {
      return [
        { name: this.insight.statItemName, title: '' } // dont want title displayed
      ]
    },
    lineChartOptions() {
      function roundToNearestTen(number, roundUp = true) {
        if (roundUp) {
          return Math.ceil(number / 10) * 10
        } else {
          return Math.floor(number / 10) * 10
        }
      }

      const accessor =
        this.insight.statItemName === 'enps' ? 'npsScore' : 'value'

      const minValue = this.insight.averages.reduce(
        (min, obj) => (obj[accessor] < min ? obj[accessor] : min),
        100
      )
      const maxValue = this.insight.averages.reduce(
        (min, obj) => (obj[accessor] > min ? obj[accessor] : min),
        -100
      )
      const ticks = maxValue - minValue > 20 ? 5 : 10

      const domain =
        this.insight.statItemName === 'participation' ? [0, 100] : [0, 10]

      const fractionalDigits =
        this.insight.statItemName === 'enps' ||
        this.insight.statItemName === 'participation'
          ? 0
          : 1

      return {
        fractionalDigits,
        height: 360,
        width: 400,
        domain:
          this.insight.statItemName === 'enps'
            ? [
                roundToNearestTen(minValue, false),
                roundToNearestTen(maxValue, true)
              ]
            : domain,
        ticks: ticks
      }
    },
    largeContainerSize() {
      if (this.shouldHideDetailedInfo) {
        return ''
      }
      if (this.isQuestion || this.hasTrend) {
        return 'col-sm-6'
      } else if (this.valueForInsight) {
        return 'col-sm-8'
      } else {
        return ''
      }
    },
    smallContainerSize() {
      if (this.isQuestion || this.hasTrend) {
        return 'col-sm-6'
      } else if (this.valueForInsight) {
        return 'col-2 col-sm-4'
      } else {
        return ''
      }
    },
    titleText() {
      const insight = this.insight
      const insightMessage = this.$tr(insight.messages, `results.alert`)
      const generalMessage = this.$tr(
        this.messages,
        `results[${insight.result}].alert`
      )
      const titleMessage = safeMessage(insightMessage) || generalMessage
      const statItemType = this.$tr(
        this.messages,
        `statItemTypes.${insight.statName}.title`
      )
      let statItemTitle = this.$tr(insight.messages, 'title')
      if (this.isQuestion) {
        statItemTitle = `"${statItemTitle}"`
      }
      return titleMessage
        ? titleMessage
            .replace(/{statItemType}/g, statItemType)
            .replace(/{statItemTitle}/g, statItemTitle)
        : `${statItemType} - ${statItemTitle}`
    },
    valueForInsight() {
      const insight = this.insight
      if (insight.statItemName === 'enps') {
        return insight.npsScore
      } else if (insight.statItemName === 'participation') {
        return valueToPercentage(insight.value)
      } else if (insight.statName === 'categories') {
        return this.$localeNumber(insight.value)
        // return insight.value.toLocaleString(this.locale, {
        //   minimumFractionDigits: 1,
        //   maximumFractionDigits: 1
        // })
      } else {
        return null
      }
    }
  },

  methods: {
    handleOpenModal() {
      const event = new Event(
        `show.puls.modal#${this.showMoreInsightsModal.id}`,
        {
          bubbles: true
        }
      )
      document.dispatchEvent(event)
    }
  }
}
</script>

<style scoped>
.hide-button-wrapper {
  position: absolute;
  top: 15px;
  right: 15px;
}
.insights-box {
  background-color: var(--kt-card-bg);
  border-radius: var(--bs-alert-border-radius);
  backdrop-filter: blur(5px);
}
.insights-box-medium-default {
  border: 3px solid var(--kt-results-yellow) !important;
}

.insights-box-high-default {
  border: 3px solid var(--kt-results-green) !important;
}

.insights-box-low-default {
  border: 3px solid var(--kt-results-red) !important;
}
.widget-title {
  font-size: 1.275rem;
  /* font-weight: 500; */
  color: var(--kt-body-color);
}
.insight-icon {
  font-size: 1.275rem;
}
.insight-icon-low {
  color: var(--kt-results-red);
}
.insight-icon-medium {
  color: var(--kt-results-yellow);
}
.insight-icon-high {
  color: var(--kt-results-green);
}
.insights-value-container {
  margin-bottom: 1rem;
}
.insights-value {
  margin-bottom: 0;
  font-weight: 400;
}
.link-button > a:hover {
  color: inherit !important;
}
.insights-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}
.insights-info-text {
  flex: 1 1 auto;
}
.has-callback:hover {
  cursor: pointer;
  border-radius: 0.95rem;
  background: var(--kt-list-group-action-active-bg);
}
</style>
